import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, MapPin, ArrowUpRight } from "lucide-react";
import { Reveal, MaskLines } from "../components/Reveal";
import { getWorldCountry, COUNTRIES } from "../data/world";

function NotFound() {
  return (
    <main className="min-h-screen bg-navy grain flex flex-col items-center justify-center text-center px-6">
      <p className="overline text-gold">Not Found</p>
      <h1 className="font-serif text-ivory text-5xl mt-4">This destination is being curated.</h1>
      <Link to="/destinations" className="btn-gold mt-10">View Destinations</Link>
    </main>
  );
}

// Horizontal, auto-advancing (5s per image) carousel of every featured place.
function PlaceCarousel({ places, country }) {
  const trackRef = useRef(null);
  const timer = useRef(null);
  const [active, setActive] = useState(0);
  const n = places.length;

  const scrollTo = useCallback((idx) => {
    const track = trackRef.current;
    if (!track) return;
    const card = track.children[idx];
    if (card) track.scrollTo({ left: card.offsetLeft - 24, behavior: "smooth" });
  }, []);

  const start = useCallback(() => {
    clearInterval(timer.current);
    timer.current = setInterval(() => {
      setActive((p) => {
        const nx = (p + 1) % n;
        scrollTo(nx);
        return nx;
      });
    }, 5000);
  }, [n, scrollTo]);

  useEffect(() => {
    start();
    return () => clearInterval(timer.current);
  }, [start]);

  const go = (dir) => {
    setActive((p) => {
      const nx = (p + dir + n) % n;
      scrollTo(nx);
      return nx;
    });
    start();
  };

  return (
    <div data-testid="place-carousel">
      <div
        ref={trackRef}
        className="flex gap-6 overflow-x-auto px-6 md:px-10 pb-6 snap-x snap-mandatory scroll-smooth"
        style={{ scrollbarWidth: "none" }}
      >
        {places.map((p, idx) => (
          <Link
            key={`${country.slug}-place-${idx}-${p.name}`}
            to={`/enquire?type=prive&destination=${encodeURIComponent(p.name + ", " + country.name)}`}
            className="group block shrink-0 snap-start w-[80vw] sm:w-[46vw] md:w-[32vw] lg:w-[24vw]"
            data-testid={`place-card-${idx}`}
          >
            <div className="relative h-[440px] overflow-hidden grain">
              <img
                src={p.img}
                alt={`${p.name}, ${country.name}`}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(13,16,56,0.82), transparent 55%)" }} />
              <div className="absolute top-4 left-4">
                <span className="font-serif text-gold/90 text-sm">{String(idx + 1).padStart(2, "0")} / {String(n).padStart(2, "0")}</span>
              </div>
              <div className="absolute bottom-0 p-6">
                <h3 className="font-serif text-3xl text-ivory group-hover:text-gold transition-colors">{p.name}</h3>
                <span className="inline-flex items-center gap-1 text-gold overline mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
                  Enquire <ArrowUpRight size={12} />
                </span>
              </div>
            </div>
          </Link>
        ))}
      </div>

      <div className="flex items-center justify-center gap-8 mt-10 px-6">
        <button onClick={() => go(-1)} data-testid="place-carousel-prev" aria-label="Previous place" className="text-ivory/70 hover:text-gold transition-colors"><ArrowLeft size={22} /></button>
        <div className="flex gap-2">
          {places.map((_, idx) => (
            <button
              key={idx}
              onClick={() => { setActive(idx); scrollTo(idx); start(); }}
              aria-label={`Go to place ${idx + 1}`}
              className="h-[3px] transition-all duration-500"
              style={{ width: idx === active ? 34 : 14, background: idx === active ? "#D4AF37" : "rgba(248,247,243,0.3)" }}
            />
          ))}
        </div>
        <button onClick={() => go(1)} data-testid="place-carousel-next" aria-label="Next place" className="text-ivory/70 hover:text-gold transition-colors"><ArrowRight size={22} /></button>
      </div>
    </div>
  );
}

export default function CountryDetail() {
  const { slug } = useParams();
  const c = getWorldCountry(slug);
  if (!c) return <NotFound />;

  const heroSrc = c.heroImg;
  const resolvedPlaces = c.places;
  const related = COUNTRIES.filter((x) => x.region === c.region && x.slug !== c.slug).slice(0, 8);

  return (
    <main data-testid="country-detail" className="bg-ivory">
      {/* Hero */}
      <section className="relative h-[100svh] overflow-hidden bg-navy">
        <motion.img src={heroSrc} alt={c.name} initial={{ scale: 1.12 }} animate={{ scale: 1 }} transition={{ duration: 2.4, ease: [0.16, 1, 0.3, 1] }} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "rgba(13,16,56,0.55)" }} />
        <div className="relative h-full max-w-[1500px] mx-auto px-6 md:px-10 flex flex-col justify-end pb-[14vh]">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4, duration: 1 }} className="overline text-gold mb-6">{c.region}</motion.p>
          <h1 className="font-serif text-ivory text-6xl md:text-[9vw] leading-[0.9] uppercase tracking-tight">
            <MaskLines lines={[c.name]} delay={0.4} />
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8, duration: 1 }} className="text-ivory/75 max-w-2xl mt-6 text-lg font-serif italic">{c.line}</motion.p>
        </div>
      </section>

      {/* Intro */}
      <section className="bg-ivory py-24 md:py-32">
        <div className="max-w-[1000px] mx-auto px-6 md:px-10">
          <Reveal><p className="overline text-charcoal/50 mb-8">The Destination</p>
            <p className="font-serif text-3xl md:text-4xl text-navy leading-[1.3]">{c.line} — a curated {c.region} journey through {c.name}, designed end to end for the discerning traveller.</p></Reveal>
        </div>
      </section>

      {/* Featured places — auto-advancing horizontal carousel */}
      <section className="bg-navy grain py-24 md:py-32" data-testid="country-places">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 mb-12">
          <Reveal><p className="overline text-gold">Featured Destinations</p>
            <h2 className="font-serif text-4xl md:text-6xl text-ivory mt-5">Places & wonders of {c.name}.</h2>
            <p className="text-ivory/55 mt-4 text-sm">{c.places.length} curated places · auto-scrolling gallery</p></Reveal>
        </div>
        <PlaceCarousel places={resolvedPlaces} country={c} />
      </section>

      {/* Signature Experiences */}
      <section className="bg-ivory py-24 md:py-32" data-testid="country-experiences">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10">
          <Reveal className="mb-14"><p className="overline text-charcoal/50">Signature Experiences</p>
            <h2 className="font-serif text-4xl md:text-6xl text-navy mt-5">Moments to remember.</h2></Reveal>
          <div className="grid md:grid-cols-2 gap-x-14 gap-y-4">
            {c.experiences.map((e, i) => (
              <Reveal key={`${c.slug}-exp-${i}`} delay={(i % 2) * 0.05}>
                <div className="flex items-center gap-5 border-b border-navy/10 py-4">
                  <span className="font-serif text-gold text-lg w-8">{String(i + 1).padStart(2, "0")}</span>
                  <span className="font-serif text-2xl text-navy">{e}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Best Time + Itineraries */}
      <section className="bg-navy grain py-24 md:py-32">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-16">
          <Reveal>
            <p className="overline text-gold mb-8">Best Time to Travel</p>
            <div className="grid grid-cols-2 gap-px bg-ivory/10">
              {c.bestTime.map((b) => (
                <div key={b.season} className="bg-navy p-6">
                  <p className="font-serif text-2xl text-ivory">{b.season}</p>
                  <p className="text-gold/80 text-sm mt-2">{b.months}</p>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="overline text-gold mb-8">Sample Itineraries</p>
            <ul className="space-y-3">
              {c.itineraries.map((it) => (
                <li key={it} className="flex items-center gap-4 border-b border-ivory/10 pb-3 text-ivory/85">
                  <span className="text-gold">—</span><span className="text-[15px]">{it}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* Travel Information */}
      <section className="bg-ivory py-24 md:py-32" data-testid="country-info">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10">
          <Reveal className="mb-12"><p className="overline text-charcoal/50">Travel Information</p></Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-navy/10">
            {[["Currency", c.info.currency], ["Language", c.info.language], ["Time Zone", c.info.timezone], ["Transportation", c.info.transport]].map(([k, v]) => (
              <Reveal key={k} className="bg-ivory"><div className="p-7 h-full"><p className="overline text-gold/80 mb-3">{k}</p><p className="text-charcoal/75 leading-relaxed text-[15px]">{v}</p></div></Reveal>
            ))}
          </div>
          <Reveal delay={0.1}><p className="text-charcoal/60 mt-8 max-w-2xl leading-relaxed"><span className="text-navy font-medium">Travel Tips — </span>{c.info.tips}</p></Reveal>
        </div>
      </section>

      {/* Map */}
      <section className="bg-navy grain py-24 md:py-32" data-testid="country-map">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10">
          <Reveal className="mb-10 flex items-center gap-3"><MapPin className="text-gold" size={18} /><p className="overline text-gold">On the Map · {c.name}</p></Reveal>
          <Reveal><div className="relative border border-gold/30 overflow-hidden h-[420px]" style={{ filter: "grayscale(0.5) contrast(1.05)" }}>
            <iframe title={`Map of ${c.name}`} src={c.mapSrc} className="w-full h-full" loading="lazy" style={{ border: 0 }} />
          </div></Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-32 md:py-44 overflow-hidden bg-navy">
        <img src={heroSrc} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover animate-kenburns" />
        <div className="absolute inset-0" style={{ background: "rgba(13,16,56,0.74)" }} />
        <div className="relative max-w-[1500px] mx-auto px-6 md:px-10 text-center">
          <Reveal>
            <p className="overline text-gold mb-6">Your Journey Begins Here</p>
            <h2 className="font-serif text-ivory text-5xl md:text-7xl max-w-[20ch] mx-auto leading-[1.05]">Tell us where you wish to go in {c.name}.</h2>
            <div className="flex flex-wrap gap-4 justify-center mt-12">
              <Link to={`/enquire?type=prive&destination=${encodeURIComponent(c.name)}`} className="btn-gold" data-testid="country-cta">Design My Bespoke Journey <ArrowRight size={16} /></Link>
              <Link to="/enquire?type=prive" className="btn-ghost text-ivory">Speak With Our Travel Designer</Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Related countries in region */}
      {related.length > 0 && (
        <section className="bg-ivory py-24">
          <div className="max-w-[1500px] mx-auto px-6 md:px-10">
            <p className="overline text-charcoal/50 mb-10">More of {c.region}</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {related.map((o) => (
                <Link key={o.slug} to={`/destinations/country/${o.slug}`} className="group block" data-testid={`related-country-${o.slug}`}>
                  <div className="relative h-[240px] overflow-hidden grain">
                    <img src={o.heroImg} alt={o.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105" />
                    <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(13,16,56,0.7), transparent 60%)" }} />
                    <h3 className="absolute bottom-4 left-4 font-serif text-2xl text-ivory">{o.name}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}
